import { Alert, Divider } from "@mui/material";
import dayjs from "dayjs";
import { useId } from "react";
import { useIntl } from "react-intl";

import { Image } from "@/components/Image/Image";
import { Skeleton } from "@/components/Skeleton/Skeleton";
import {
  DetailsList,
  DetailsListItem,
} from "@/features/trips/components/DetailsList";
import type { TripDetails } from "@/features/trips/types/types";

type Props = {
  trip: TripDetails;
  startDate: string | undefined;
};

export function OverviewCard({ trip, startDate }: Props) {
  const intl = useIntl();
  const alertTitleId = useId();

  const startDateFormatted = dayjs(startDate).format("D MMMM, YYYY");
  const endDateFormatted = dayjs(startDate)
    .add(trip.durationInDays, "day")
    .format("D MMMM, YYYY");

  return (
    <div className="rounded-3xl bg-white p-1">
      <Image
        src={trip.imageUrls[0]}
        alt={trip.name}
        className="aspect-7/2 w-full rounded-t-2xl object-cover"
      />

      <div className="p-3">
        <div className="mb-1 text-xl font-medium text-text-title">
          {trip.name}
        </div>
        <div className="text-sm text-text-tertiary">{trip.subtitle}</div>

        <Divider
          sx={{
            borderColor: "var(--color-stroke-neutral-soft)",
            my: "calc(var(--spacing)*3)",
          }}
        />

        <DetailsList>
          <DetailsListItem
            title={intl.formatMessage({ id: "trips.travelStartDate" })}
            value={
              startDate ? (
                startDateFormatted
              ) : (
                <Skeleton>
                  <span>{startDateFormatted}</span>
                </Skeleton>
              )
            }
          />

          <DetailsListItem
            title={intl.formatMessage({ id: "trips.travelEndDate" })}
            value={
              startDate ? (
                endDateFormatted
              ) : (
                <Skeleton>
                  <span>{endDateFormatted}</span>
                </Skeleton>
              )
            }
          />
        </DetailsList>

        <Alert
          severity="info"
          sx={{
            mt: "calc(var(--spacing)*4)",
          }}
          aria-labelledby={alertTitleId}
        >
          <div
            className="mb-1 text-sm font-medium text-black"
            id={alertTitleId}
          >
            {intl.formatMessage({ id: "trips.bookingInfo.title" })}
          </div>
          <div className="text-sm text-text-placeholder">
            {intl.formatMessage({ id: "trips.bookingInfo.content" })}
          </div>
        </Alert>
      </div>
    </div>
  );
}
